<!-- app/javascript/dashboard/routes/dashboard/settings/emailTemplates/builder/PublishGalleryButton.vue -->
<script setup>
// Só super admin: "Publicar na galeria". Salva, pega a prévia do servidor,
// gera a miniatura (PNG 600px) no navegador e publica/atualiza o modelo
// global. Mesma chave = atualiza o modelo já publicado.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import API from 'dashboard/api/marketingEmailTemplates';
import { makeThumbnail } from './thumbnail';

const props = defineProps({
  templateId: { type: [String, Number], required: true },
  templateName: { type: String, default: '' },
  source: { type: Function, required: true },
  beforePublish: { type: Function, default: null },
});
const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const SEGMENTS = ['general', 'real_estate', 'health', 'education', 'retail'];
const GOALS = [
  'welcome',
  'promo',
  'newsletter',
  'event',
  'post_sale',
  'reactivation',
  'seasonal',
  'launch',
  'appointment',
  'other',
];
const dialog = ref(null);
const busy = ref(false);
const EMPTY_FORM = {
  name: '',
  description: '',
  segment: 'general',
  goal: 'other',
  key: '',
};
const form = ref({ ...EMPTY_FORM });

const open = () => {
  form.value = { ...form.value, name: form.value.name || props.templateName };
  dialog.value?.open();
};
const publish = async () => {
  if (busy.value) return;
  const name = form.value.name.trim();
  if (!name) {
    useAlert(t('EMAIL_TEMPLATES.EDITOR.PUBLISH.NAME_REQUIRED'));
    return;
  }
  busy.value = true;
  try {
    await props.beforePublish?.();
    const { data: preview } = await API.preview(props.source());
    let thumbnail = null;
    try {
      thumbnail = await makeThumbnail(preview.html);
    } catch (error) {
      useAlert(t('EMAIL_TEMPLATES.EDITOR.PUBLISH.NO_THUMB'));
    }
    await API.publishGallery(props.templateId, {
      name,
      description: form.value.description.trim(),
      segment: form.value.segment,
      goal: form.value.goal,
      key: form.value.key.trim() || undefined,
      thumbnail,
    });
    useAlert(t('EMAIL_TEMPLATES.EDITOR.PUBLISH.DONE'));
    dialog.value?.close();
    form.value = { ...EMPTY_FORM };
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.PUBLISH.ERROR')
    );
  } finally {
    busy.value = false;
  }
};
const inputClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12';
</script>

<template>
  <template v-if="isSuperAdmin">
    <button
      type="button"
      data-test="publish-gallery"
      class="rounded-lg border border-n-weak px-2.5 py-1.5 text-sm text-n-slate-11 hover:bg-n-solid-2"
      @click="open"
    >
      {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.BUTTON') }}
    </button>
    <Dialog
      ref="dialog"
      :title="t('EMAIL_TEMPLATES.EDITOR.PUBLISH.TITLE')"
      :confirm-button-label="t('EMAIL_TEMPLATES.EDITOR.PUBLISH.CONFIRM')"
      :is-loading="busy"
      @confirm="publish"
    >
      <div class="flex flex-col gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.NAME') }}
          <input
            v-model="form.name"
            data-test="name"
            maxlength="120"
            :class="inputClass"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.DESCRIPTION') }}
          <textarea
            v-model="form.description"
            data-test="description"
            rows="2"
            maxlength="300"
            :class="inputClass"
          />
        </label>
        <div class="flex gap-2">
          <label class="flex flex-1 flex-col gap-1 text-xs text-n-slate-11">
            {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.SEGMENT') }}
            <select
              v-model="form.segment"
              data-test="segment"
              :class="inputClass"
            >
              <option v-for="s in SEGMENTS" :key="s" :value="s">
                {{ t(`EMAIL_TEMPLATES.GALLERY.SEGMENTS.${s}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-1 flex-col gap-1 text-xs text-n-slate-11">
            {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.GOAL') }}
            <select v-model="form.goal" data-test="goal" :class="inputClass">
              <option v-for="g in GOALS" :key="g" :value="g">
                {{ t(`EMAIL_TEMPLATES.GALLERY.GOALS.${g}`) }}
              </option>
            </select>
          </label>
        </div>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.KEY') }}
          <input
            v-model="form.key"
            data-test="key"
            maxlength="80"
            :class="inputClass"
          />
        </label>
        <p v-if="busy" class="text-xs text-n-slate-10">
          {{ t('EMAIL_TEMPLATES.EDITOR.PUBLISH.WORKING') }}
        </p>
      </div>
    </Dialog>
  </template>
</template>
